import { useState, useCallback } from 'react'
import { Box, Button, Container, Divider, Typography } from '@mui/material'
import { httpsCallable } from 'firebase/functions'

import { functions } from '../../firebase'
import { ReactComponent as RubricIcon } from '../../assets/icons/magic_sparkles.svg'
import { ReactComponent as EditIcon } from '../../assets/icons/rubric_edit.svg'

import { GenerateWithAIForm } from './GenerateWithAIForm'

import useUtils from '@/hooks/useUtilities'
import { pathUploadDocument, uploadFile } from '@/utils/upload-file'
import { useAuthUser } from '@/hooks/useUser'

export const rubricExample = {
  name: 'Rubric Name',
  description: 'Rubric Description',
  score_titles: {
    0: 'Score 1 Title (Example: beginner, poor, etc) ',
    1: 'Score 2 Title (Example: intermediate, good, etc)',
    2: 'Score 3 Title (Example: advanced, excellent, etc)',
  },
  criteria: [
    {
      title: 'Criterion 1',
      criterion_id: 'criterion_1',
      description: 'Criterion 1 Description',
      score_definitions: [
        {
          level: 0,
          definition: 'Score 1 Definition',
          score_id: 'score_1',
        },
        {
          level: 1,
          definition: 'Score 2 Definition',
          score_id: 'score_2',
        },
        {
          level: 2,
          definition: 'Score 3 Definition',
          score_id: 'score_3',
        },
      ],
    },
    {
      title: 'Criterion 2',
      criterion_id: 'criterion_2',
      description: 'Criterion 2 Description',
      score_definitions: [
        {
          level: 0,
          definition: 'Score 1 Definition',
          score_id: 'score_1',
        },
        {
          level: 1,
          definition: 'Score 2 Definition',
          score_id: 'score_2',
        },
        {
          level: 2,
          definition: 'Score 3 Definition',
          score_id: 'score_3',
        },
      ],
    },
  ],
}

function GenerateRubric({ onGenerateRubric = () => {}, context, onCreateFromScratch = () => {} }) {
  const { showSnackbar } = useUtils()
  const parsePdfFunction = httpsCallable(functions, 'parsePdfFunction')
  const generateRubric = httpsCallable(functions, 'generateRubric', { timeout: 300000 })
  const uid = useAuthUser().uid

  const [rubricDescription, setRubricDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [pdfFile, setPdfFile] = useState(null)
  const [pdfText, setPdfText] = useState('')
  const [progress, setProgress] = useState(0)

  const handleGenerate = useCallback(
    async withImage => {
      setLoading(true)

      try {
        const response = await generateRubric({
          pdfText,
          rubricDescription,
          prompt: context?.prompt,
          imageUrl: context?.image?.url,
          rubricExample,
        })
        const rubric = response?.data?.rubric

        onGenerateRubric({ ...rubric, format: 'table' })
      } catch (err) {
        console.error('Error generating rubric:', err)
        showSnackbar('We’re sorry, but we ran into an issue. Please try again in a moment.', 'error')
      } finally {
        setLoading(false)
      }
    },
    [context, onGenerateRubric, pdfText, rubricDescription, showSnackbar],
  )

  const handlePdfChange = async e => {
    const file = e.target.files?.[0]

    if (!file) return

    setPdfFile(file)
    setUploadingPdf(true)
    setProgress(0)

    try {
      const path = pathUploadDocument(uid, file.name)
      const { storagePath } = await uploadFile(file, path)
      const { data } = await parsePdfFunction({ filePath: storagePath })

      setPdfText(data.text)
      setProgress(100)
    } catch (err) {
      console.error('Error uploading/parsing PDF:', err)
      showSnackbar('Error uploading/parsing PDF. Please try again.', 'error')
    } finally {
      setUploadingPdf(false)
    }
  }

  const handleRemovePdf = () => {
    setPdfFile(null)
    setPdfText('')
    setProgress(0)
  }

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        justifyContent: 'center',
        height: '80vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <RubricIcon />
          <Typography fontSize={30} fontWeight={600}>
            Use AI to generate your rubric
          </Typography>
        </Box>

        <GenerateWithAIForm
          buttonText="Generate"
          handlePdfChange={handlePdfChange}
          imageUrl={context?.image?.url}
          loading={loading}
          pdfFile={pdfFile}
          progress={progress}
          type="create"
          uploadingPdf={uploadingPdf}
          value={rubricDescription}
          onChange={e => setRubricDescription(e.target.value)}
          onRemovePdf={handleRemovePdf}
          onSubmit={handleGenerate}
        />
        <Box width="100%">
          <Divider>OR</Divider>
        </Box>
        <Box>
          <Button
            startIcon={<EditIcon style={{ width: '20px', height: '20px' }} />}
            sx={{ fontWeight: 600, fontSize: 16 }}
            variant="text"
            onClick={onCreateFromScratch}
          >
            Create from Scratch
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default GenerateRubric
